'use client';

import { useEffect, useState } from 'react';
import { PriceData, PriceDataDetails, Product } from '@/models/Product';
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from './ui/carousel';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { calculateDiscountPercentage, formatNumberToPriceText } from '@/lib/utils';
import Image from 'next/image';
import Link from 'next/link';
import SmartImage from './SmartImage';
import { IMAGE_PLACEHOLDER } from '@/lib/Constants';

export default function AllowedProductListSwiper({
  products,
  priceData,
  width,
  swiperText,
  swiperImage,
}: {
  products?: Product[];
  priceData: PriceData | null;
  width?: number;
  swiperText?: string;
  swiperImage?: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <div className="w-full flex flex-col items-center">
      <Carousel
        className="w-full relativer"
        style={{ maxWidth: width ? `${width}px` : '' }}
        setApi={setApi}
        opts={{
          align: 'center',
          loop: true,
          skipSnaps: false,
          containScroll: 'trimSnaps',
        }}
      >
        <CarouselContent>
          {products?.map((product, index) => (
            <CarouselItem key={index}>
              <div className="flex flex-col gap-2">
                <AllowedProductCard
                  key={product.id}
                  product={product}
                  price={priceData?.[product.id] || null}
                  swiperText={swiperText}
                  swiperImage={swiperImage}
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <div className="flex w-full justify-between mt-4 items-center">
        <div
          className="size-8 bg-[#FEE7EA] border-none hover:bg-[#FEE7EA] text-[#AF0E1D] rounded-full flex items-center justify-center hover:scale-110 transition-all duration-300 hover:cursor-pointer"
          onClick={() => api?.scrollPrev()}
        >
          <ChevronLeftIcon className="size-4" />
        </div>
        <div className="text-[12px] font-normal text-[#5C5C57]">
          Ürün {current} / {count}
        </div>

        <div
          className="size-8 bg-[#FEE7EA] border-none hover:bg-[#FEE7EA] text-[#AF0E1D] rounded-full flex items-center justify-center hover:scale-110 transition-all duration-300 hover:cursor-pointer"
          onClick={() => api?.scrollNext()}
        >
          <ChevronRightIcon className="size-4" />
        </div>
      </div>
    </div>
  );
}

function AllowedProductCard({
  product,
  price,
  className,
  swiperText,
  swiperImage,
}: {
  product: Product;
  price: PriceDataDetails | null;
  className?: string;
  swiperText?: string;
  swiperImage?: string;
}) {
  return (
    <Link
      href={product.url}
      className={`mx-auto w-full flex hover:cursor-pointer flex-col rounded-2xl border border-[#F1EFE9]`}
    >
      <div className={className || 'relative rounded-2xl'}>
        <SmartImage
          width={696}
          height={23}
          alt={product.title}
          imageType="product" // ✅ EKLE
          className="rounded-2xl aspect-square h-auto mx-auto"
          style={{
            objectFit: 'cover',
            width: '60%',
            height: 'auto',
          }}
          priority={true}
          src={product.imageUrl || IMAGE_PLACEHOLDER}
          quality={80}
        />

        {swiperText && (
          <div className="absolute bottom-16 -rotate-6 flex w-full h-[24px] items-center justify-center bg-[#FFE4E1]">
            <span className="font-semibold text-[13px] text-[#AF0E1D] font-fraunces">
              {swiperText}
            </span>
            {swiperImage && (
              <SmartImage
                src={swiperImage}
                alt={swiperText}
                width={48}
                height={27}
                imageType="icon" // ✅ EKLE
                className="absolute -top-3 right-2 sm:right-4 md:right-6 lg:right-8"
              />
            )}
          </div>
        )}
      </div>
      <div className={`py-2 px-4`}>
        <div
          className={
            'font-semibold text-[16px] sm:text-[20px] text-[#212121] line-clamp-1 font-fraunces'
          }
        >
          {product.title}
        </div>
        <div className="flex flex-row items-center gap-2">
          <div className="flex flex-col">
            {typeof price?.priceBeforeDiscount === 'number' &&
              price?.priceBeforeDiscount !== price?.price &&
              price?.priceBeforeDiscount > 0 && (
                <div className="font-bold text-[12px] sm:text-[16px] text-[#5C5C57] relative w-fit font-fraunces">
                  <div className="absolute w-full h-0.5 bg-[#5C5C57B2] bg-opacity-70 rounded-full bottom-1/2" />
                  {formatNumberToPriceText(price?.priceBeforeDiscount)} TL
                </div>
              )}
            {price && price?.price ? (
              <div
                className={
                  'font-bold text-[24px] sm:text-[32px] font-fraunces text-[#AF0E1D] leading-[1.2]'
                }
              >
                {formatNumberToPriceText(price?.price)} TL
              </div>
            ) : (
              <div className="font-semibold text-lg text-hzc_purple_500">&nbsp;</div>
            )}
            {!(
              typeof price?.priceBeforeDiscount === 'number' &&
              price?.priceBeforeDiscount !== price?.price &&
              price?.priceBeforeDiscount > 0
            ) && <div className="h-6" />}
          </div>
        </div>
      </div>
      {typeof price?.priceBeforeDiscount === 'number' &&
        price?.priceBeforeDiscount !== price?.price &&
        price?.priceBeforeDiscount > 0 &&
        calculateDiscountPercentage(price?.price || 0, price?.priceBeforeDiscount || 0) > 10 && (
          <div className="absolute top-0 right-6 flex flex-col h-[48px] items-center justify-center rounded-b-[12px] bg-[#FFE4E1] px-4">
            <span className="font-bold text-[21px] text-[#212121] font-fraunces">
              -%
              {calculateDiscountPercentage(price?.price || 0, price?.priceBeforeDiscount || 0)}
            </span>
          </div>
        )}
    </Link>
  );
}
// test
